import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { MessageSquare, Send, X, ArrowLeft, Loader2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* iter-80 — MessagesDrawer.
   In-app user-to-user messaging. The drawer lives globally and opens
   via the `atrium:open-messages` window event (Header button + any
   future surface). Two views:
     • Inbox: conversations grouped by counter-party with unread badge
     • Thread: scrollable message list + composer pinned to the bottom
   Polling every 12s keeps the unread count fresh without WebSockets —
   appropriate for hospitality back-office use (not real-time chat). */
export default function MessagesDrawer({ open, onClose }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [view, setView] = useState('inbox'); // 'inbox' | 'compose' | 'thread'
  const [convs, setConvs] = useState([]);
  const [activeEmail, setActiveEmail] = useState(null);
  const [activeName, setActiveName] = useState('');
  const [thread, setThread] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactFilter, setContactFilter] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const loadInbox = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/messages/inbox`, auth);
      setConvs(data.conversations || []);
    } catch (e) { /* silent — drawer can be closed */ }
  }, [token]);

  const loadContacts = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/messages/contacts`, auth);
      setContacts(data.contacts || []);
    } catch (e) { /* silent */ }
  }, [token]);

  const loadThread = useCallback(async (email) => {
    if (!token || !email) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/messages/thread/${encodeURIComponent(email)}`, auth);
      setThread(data.messages || []);
      // refresh inbox so the unread badge clears
      loadInbox();
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load conversation.');
    } finally {
      setLoading(false);
    }
  }, [token, loadInbox]);

  // Re-load inbox + reset view on open.
  useEffect(() => {
    if (open) {
      setView('inbox');
      setActiveEmail(null);
      setBody('');
      loadInbox();
      loadContacts();
    }
  }, [open, loadInbox, loadContacts]);

  const openThread = (email, name) => {
    setActiveEmail(email);
    setActiveName(name || email);
    setView('thread');
    setThread([]);
    loadThread(email);
  };

  const send = async () => {
    if (!body.trim() || !activeEmail) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { to_user_email: activeEmail, body: body.trim() }, auth);
      setBody('');
      await loadThread(activeEmail);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send message.');
    } finally {
      setSending(false);
    }
  };

  const filteredContacts = contacts.filter((c) => {
    const q = contactFilter.toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q) || (c.title || '').toLowerCase().includes(q);
  });

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-lg w-[95vw] p-0 overflow-hidden flex flex-col"
        style={{ height: 'min(80vh, 720px)' }}
        data-testid="messages-drawer"
      >
        <DialogTitle className="sr-only">Atrium Messages</DialogTitle>
        <DialogDescription className="sr-only">Send and read messages with your teammates.</DialogDescription>

        {/* Header */}
        <div className="px-5 py-3 flex items-center gap-3 flex-shrink-0" style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #003DA5 100%)', fontFamily: 'Manrope' }}>
          {view !== 'inbox' && (
            <button
              onClick={() => { setView('inbox'); loadInbox(); }}
              data-testid="messages-back"
              className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <MessageSquare className="w-5 h-5 text-white" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#C7D2FE' }}>Atrium · Messages</p>
            <p className="text-sm font-bold text-white truncate">
              {view === 'inbox' && 'Inbox'}
              {view === 'compose' && 'New conversation'}
              {view === 'thread' && (activeName || activeEmail)}
            </p>
          </div>
          <button onClick={onClose} type="button" aria-label="Close" className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" data-testid="messages-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        {!token && (
          <div className="flex-1 flex items-center justify-center p-6 text-sm text-center" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
            Sign in to use Atrium Messages.
          </div>
        )}

        {token && view === 'inbox' && (
          <>
            <div className="flex-1 overflow-y-auto" data-testid="messages-inbox-list">
              {convs.length === 0 ? (
                <div className="p-6 text-sm text-center" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  No conversations yet — start one below.
                </div>
              ) : (
                convs.map((c) => (
                  <button
                    key={c.counter_email}
                    onClick={() => openThread(c.counter_email, c.counter_name)}
                    data-testid={`messages-conv-${c.counter_email}`}
                    className="w-full text-left px-4 py-3 border-b hover:bg-indigo-50 flex items-start gap-3"
                    style={{ borderColor: '#E5E7EB', fontFamily: 'Manrope' }}
                  >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1F4FBE', color: 'white', fontSize: 13, fontWeight: 700 }}>
                      {(c.counter_name || c.counter_email || '?').trim()[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E' }}>{c.counter_name || c.counter_email}</p>
                        {c.unread_count > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#DC2626', color: 'white' }}>
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs truncate" style={{ color: c.unread_count > 0 ? '#1A2E2E' : '#7A8C8C', fontWeight: c.unread_count > 0 ? 600 : 400 }}>
                        {c.last_from_self ? 'You: ' : ''}{c.last_message}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="p-3 border-t flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
              <button
                onClick={() => setView('compose')}
                data-testid="messages-new-btn"
                className="w-full px-4 py-2.5 rounded-lg text-xs font-bold text-white flex items-center justify-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #6366F1 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}
              >
                <Send className="w-3.5 h-3.5" /> New message
              </button>
            </div>
          </>
        )}

        {token && view === 'compose' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-3 border-b flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#7A8C8C' }} />
                <input
                  value={contactFilter}
                  onChange={(e) => setContactFilter(e.target.value)}
                  data-testid="messages-contact-search"
                  placeholder="Search teammates by name, email or title"
                  className="w-full pl-8 pr-3 py-2 rounded-lg border text-sm"
                  style={{ borderColor: '#D9E3F1', fontFamily: 'Manrope' }}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredContacts.length === 0 ? (
                <div className="p-6 text-sm text-center" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  No teammates match.
                </div>
              ) : (
                filteredContacts.map((c) => (
                  <button
                    key={c.email}
                    onClick={() => openThread(c.email, c.name)}
                    data-testid={`messages-contact-${c.email}`}
                    className="w-full text-left px-4 py-3 border-b hover:bg-indigo-50 flex items-center gap-3"
                    style={{ borderColor: '#E5E7EB', fontFamily: 'Manrope' }}
                  >
                    {c.photo_url ? (
                      <img src={c.photo_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1F4FBE', color: 'white', fontSize: 13, fontWeight: 700 }}>
                        {(c.name || c.email || '?').trim()[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E' }}>{c.name || c.email}</p>
                      <p className="text-[11px] truncate" style={{ color: '#7A8C8C' }}>{c.title || c.role || c.email}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {token && view === 'thread' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" style={{ backgroundColor: '#F8FAFC' }} data-testid="messages-thread">
              {loading && <div className="text-center text-xs py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
              {!loading && thread.length === 0 && (
                <div className="text-center text-xs py-6" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  No messages yet — say hello.
                </div>
              )}
              {thread.map((m) => {
                const me = (m.from_email || '').toLowerCase() === (localStorage.getItem('user_email') || '').toLowerCase()
                  || m.from_email !== activeEmail; // fallback: if not from active counter-party, it's me
                return (
                  <div key={m.id} className={`flex ${me ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className="max-w-[80%] px-3 py-2 rounded-2xl"
                      style={{
                        backgroundColor: me ? '#1F4FBE' : 'white',
                        color: me ? 'white' : '#1A2E2E',
                        border: me ? 'none' : '1px solid #E5E7EB',
                        fontFamily: 'Manrope',
                      }}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                      <p className="text-[9px] mt-1" style={{ color: me ? 'rgba(255,255,255,0.7)' : '#9CA3AF' }}>
                        {new Date(m.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t flex-shrink-0 flex items-end gap-2" style={{ borderColor: '#E5E7EB' }}>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                data-testid="messages-compose-textarea"
                placeholder="Message…"
                rows={2}
                className="flex-1 px-3 py-2 rounded-lg border text-sm resize-none"
                style={{ borderColor: '#D9E3F1', fontFamily: 'Manrope' }}
              />
              <button
                onClick={send}
                disabled={sending || !body.trim()}
                data-testid="messages-send-btn"
                className="px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 flex items-center gap-1"
                style={{ background: 'linear-gradient(135deg, #6366F1 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
